/**
 * Vortex Portal Renderer — draws void portals with thick Cthulhu-style
 * tentacles, tentacle lash animations, and collapse grab visuals.
 */

import { Graphics, Container } from 'pixi.js';
import type { GameState, VortexHole } from '../types';
import { VORTEX } from '../constants';

/** Active tentacle lash animation (visual only). */
interface TentacleLashAnim {
  portalX: number;
  portalY: number;
  targetX: number;
  targetY: number;
  frame: number;
  maxFrames: number;
}

export class VortexHoleRenderer {
  private darkGfx: Graphics;
  private gfx: Graphics;
  private time = 0;
  private tentacleLashes: TentacleLashAnim[] = [];

  constructor(worldContainer: Container) {
    this.darkGfx = new Graphics();
    worldContainer.addChild(this.darkGfx);
    this.gfx = new Graphics();
    this.gfx.blendMode = 'add';
    worldContainer.addChild(this.gfx);
  }

  /** Trigger a tentacle lash animation (called from effects system). */
  triggerTentacleLash(portalX: number, portalY: number, targetX: number, targetY: number): void {
    this.tentacleLashes.push({
      portalX, portalY, targetX, targetY,
      frame: 0,
      maxFrames: VORTEX.TENTACLE_ANIM_FRAMES,
    });
  }

  update(state: GameState, dt: number): void {
    this.gfx.clear();
    this.darkGfx.clear();
    this.time += dt;

    // Draw portals (with idle tentacles)
    for (const hole of state.vortexHoles) {
      this.drawPortal(hole);
    }

    // Draw tentacle lash animations (S1 defense)
    this.updateTentacleLashes();

    // Draw collapse visuals (S2 grab)
    if (state.collapseActive) {
      this.drawCollapseEffect(state);
    }
  }

  private drawPortal(hole: VortexHole): void {
    const { x, y } = hole;

    // Outer void mouth — dark center with subtle pulsing rim
    const mouthRadius = 18;
    const breathe = 1 + Math.sin(this.time * 2.5) * 0.08;

    // Dark core (normal blend — additive makes dark colors invisible)
    this.darkGfx.circle(x, y, mouthRadius * breathe);
    this.darkGfx.fill({ color: 0x110022, alpha: 0.85 });

    // Inner singularity point
    this.darkGfx.circle(x, y, 4);
    this.darkGfx.fill({ color: 0x000000, alpha: 0.95 });

    // Glow ring
    const glowPulse = 0.35 + Math.sin(this.time * 3) * 0.12;
    this.gfx.circle(x, y, mouthRadius * breathe + 2);
    this.gfx.stroke({ color: 0x9944ff, width: 2.5, alpha: glowPulse });

    // Idle Cthulhu tentacles — 5 thick, slow-writhing arms reaching out from the portal
    const idleCount = 5;
    for (let i = 0; i < idleCount; i++) {
      const baseAngle = (i * Math.PI * 2) / idleCount + this.time * 0.4;
      const tentLen = 30 + Math.sin(this.time * 1.2 + i * 1.7) * 12;
      this.drawCthulhuTentacle(
        x, y, baseAngle, mouthRadius, tentLen,
        6, 1.5,   // baseThick, tipThick
        0.3,      // alpha
        i,        // seed offset for wobble variation
        3.0,      // wobble speed
        12,       // wobble amplitude
      );
    }

    // Lifetime pips
    const pipCount = hole.turnsRemaining;
    const pipRadius = 26;
    for (let i = 0; i < pipCount; i++) {
      const pipAngle = -Math.PI / 2 + (i * Math.PI * 2) / VORTEX.PORTAL_LIFETIME_TURNS;
      const px = x + Math.cos(pipAngle) * pipRadius;
      const py = y + Math.sin(pipAngle) * pipRadius;
      this.gfx.circle(px, py, 2);
      this.gfx.fill({ color: 0xaa44ff, alpha: 0.6 });
    }

    // Tentacle range indicator (very subtle)
    const rangeAlpha = 0.04 + Math.sin(this.time * 1.5) * 0.015;
    this.gfx.circle(x, y, VORTEX.TENTACLE_RANGE);
    this.gfx.stroke({ color: 0x6622aa, width: 1, alpha: rangeAlpha });
  }

  // ─── Cthulhu tentacle primitive ─────────────────────────────────────────────
  // A thick, organic tentacle drawn as a multi-pass polyline with sinusoidal
  // wobble that increases toward the tip. Used by idle wisps, lash, and collapse.

  private drawCthulhuTentacle(
    originX: number, originY: number,
    angle: number,          // direction from origin
    startDist: number,      // how far from origin the tentacle starts (portal radius)
    length: number,         // total tentacle length
    baseThick: number,      // thickness at root
    tipThick: number,       // thickness at tip
    alpha: number,          // overall alpha
    seed: number,           // per-tentacle variation
    wobbleSpeed: number,    // wobble animation speed
    wobbleAmp: number,      // max perpendicular displacement
  ): void {
    const segments = 12;
    const points: { x: number; y: number }[] = [];

    // Direction vector
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    // Perpendicular
    const px = -dy;
    const py = dx;

    for (let s = 0; s <= segments; s++) {
      const t = s / segments;
      const dist = startDist + t * length;

      // Wobble increases toward tip, with multiple sine layers for organic look
      const wobble1 = Math.sin(this.time * wobbleSpeed + t * 5 + seed * 2.1) * wobbleAmp * t;
      const wobble2 = Math.sin(this.time * wobbleSpeed * 1.7 + t * 8 + seed * 3.3) * wobbleAmp * 0.4 * t;
      const totalWobble = wobble1 + wobble2;

      points.push({
        x: originX + dx * dist + px * totalWobble,
        y: originY + dy * dist + py * totalWobble,
      });
    }

    // Draw 3 passes: dark outer glow, mid purple, bright core
    const passes = [
      { thickMult: 2.2, color: 0x330066, alphaMult: 0.3 },
      { thickMult: 1.4, color: 0x7733cc, alphaMult: 0.6 },
      { thickMult: 1.0, color: 0xaa55ff, alphaMult: 1.0 },
    ];

    for (const pass of passes) {
      for (let s = 0; s < points.length - 1; s++) {
        const t = s / (points.length - 1);
        const thick = (baseThick + (tipThick - baseThick) * t) * pass.thickMult;
        const segAlpha = alpha * pass.alphaMult * (1 - t * 0.4);

        this.gfx.moveTo(points[s].x, points[s].y);
        this.gfx.lineTo(points[s + 1].x, points[s + 1].y);
        this.gfx.stroke({ color: pass.color, width: thick, alpha: segAlpha, cap: 'round', join: 'round' });
      }
    }

    // Sucker dots along the tentacle (Cthulhu detail)
    for (let s = 2; s < points.length - 1; s += 2) {
      const t = s / (points.length - 1);
      const suckerSize = (baseThick * 0.3) * (1 - t * 0.5);
      const suckerAlpha = alpha * 0.5 * (1 - t * 0.3);
      this.gfx.circle(points[s].x, points[s].y, suckerSize);
      this.gfx.fill({ color: 0xcc77ff, alpha: suckerAlpha });
    }
  }

  // ─── Tentacle lash (S1 proximity defense) ─────────────────────────────────

  private updateTentacleLashes(): void {
    for (let i = this.tentacleLashes.length - 1; i >= 0; i--) {
      const lash = this.tentacleLashes[i];
      lash.frame++;

      const progress = lash.frame / lash.maxFrames;
      const dx = lash.targetX - lash.portalX;
      const dy = lash.targetY - lash.portalY;
      const angle = Math.atan2(dy, dx);
      const totalDist = Math.sqrt(dx * dx + dy * dy);

      if (progress <= 0.4) {
        // Extend phase — tentacle shoots out from portal toward target
        const reach = progress / 0.4;
        const eased = reach * reach * (3 - 2 * reach); // smoothstep
        this.drawCthulhuTentacle(
          lash.portalX, lash.portalY,
          angle, 18, totalDist * eased,
          10, 3,    // thick at root, tapered tip
          0.9,      // bright
          i * 7.1,  // seed
          6.0,      // fast wobble during strike
          18,       // big wobble amplitude
        );
      } else if (progress <= 0.65) {
        // Strike hold — full extension, writhing at target
        this.drawCthulhuTentacle(
          lash.portalX, lash.portalY,
          angle, 18, totalDist,
          10, 4,
          1.0,
          i * 7.1,
          8.0,      // frantic wobble
          22,       // intense amplitude
        );
        // Impact flash at target
        const flash = 1 - (progress - 0.4) / 0.25;
        this.gfx.circle(lash.targetX, lash.targetY, 12 + flash * 8);
        this.gfx.fill({ color: 0xcc88ff, alpha: flash * 0.6 });
      } else {
        // Retract phase — tentacle pulls back into portal
        const retract = (progress - 0.65) / 0.35;
        const eased = retract * retract;
        const remainDist = totalDist * (1 - eased);
        this.drawCthulhuTentacle(
          lash.portalX, lash.portalY,
          angle, 18, Math.max(0, remainDist),
          10 * (1 - eased), 2,
          0.7 * (1 - eased),
          i * 7.1,
          4.0,
          14,
        );
      }

      if (lash.frame >= lash.maxFrames) {
        this.tentacleLashes.splice(i, 1);
      }
    }
  }

  // ─── Collapse effect (S2 grab + launch) ───────────────────────────────────

  private drawCollapseEffect(state: GameState): void {
    for (const portal of state.collapsePortals) {
      // Violent portal implosion — pulsing dark core (normal blend)
      const collapseBreath = 1 + Math.sin(this.time * 12) * 0.2;
      this.darkGfx.circle(portal.x, portal.y, 28 * collapseBreath);
      this.darkGfx.fill({ color: 0x220044, alpha: 0.6 });
      this.darkGfx.circle(portal.x, portal.y, 16 * collapseBreath);
      this.darkGfx.fill({ color: 0x000000, alpha: 0.8 });

      // Grabbing tentacles reaching toward each grabbed player
      for (let pi = 0; pi < state.players.length; pi++) {
        const p = state.players[pi];
        if (!p.collapseGrabbed) continue;

        const dx = p.x - portal.x;
        const dy = p.y - portal.y;
        const angle = Math.atan2(dy, dx);
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Main grab tentacle — thick and aggressive
        this.drawCthulhuTentacle(
          portal.x, portal.y,
          angle, 18, Math.max(0, dist - 10),
          12, 5,     // very thick root
          0.85,
          pi * 5.3,
          10.0,      // violent wobble
          20,
        );

        // Secondary flanking tentacle (offset angle for claw-like grab)
        this.drawCthulhuTentacle(
          portal.x, portal.y,
          angle - 0.2, 18, Math.max(0, dist * 0.85),
          8, 2,
          0.5,
          pi * 5.3 + 11,
          8.0,
          15,
        );
        this.drawCthulhuTentacle(
          portal.x, portal.y,
          angle + 0.2, 18, Math.max(0, dist * 0.85),
          8, 2,
          0.5,
          pi * 5.3 + 23,
          8.0,
          15,
        );
      }

      // Ambient flailing tentacles (even when no targets grabbed — portal is collapsing)
      for (let i = 0; i < 4; i++) {
        const flailAngle = this.time * 2.5 + i * Math.PI * 0.5;
        const flailLen = 40 + Math.sin(this.time * 4 + i * 2) * 20;
        this.drawCthulhuTentacle(
          portal.x, portal.y,
          flailAngle, 18, flailLen,
          7, 1.5,
          0.35,
          i * 13.7,
          7.0,
          25,
        );
      }
    }
  }

  destroy(): void {
    this.darkGfx.destroy();
    this.gfx.destroy();
  }
}
